// Copyright 2025 Sorama B.V.
import { DrawImageProgram } from "controls/plot/programs/DrawImageProgram";
import { GenericMeasurement, SpectrumSetAnalysisProperties, SpectrumSetSpectrum } from "datatypes/SoramaWebApi";
import { IMeasurementProcessorResolver } from "services/Measurement/IMeasurementProcessorResolver";
import { IPsychoAcousticsProcessingService } from "services/SoundProcessing/IPsychoAcousticsProcessingService";
import { Time } from "units/Time";
import {
    PsychoAcousticBandUnits,
    PsychoAcousticMetricType,
    PsychoAcousticUnits,
} from "../../PsychoAcoustics/SpiderChart/SpiderChart";
import { ISpectrogramProvider, UnitSpectrogram } from "./ISpectrogramProvider";

export class PsychoacousticsSpectrogramProvider implements ISpectrogramProvider {
    constructor(
        private readonly processorResolver: IMeasurementProcessorResolver,
        private readonly metricType: PsychoAcousticMetricType
    ) {}

    public async getSpectrumSet(
        measurement: GenericMeasurement,
        properties: SpectrumSetAnalysisProperties,
        progressCallback: (progress: number) => void
    ): Promise<UnitSpectrogram> {
        const processor = await this.processorResolver.resolveProcessor<IPsychoAcousticsProcessingService>(
            IPsychoAcousticsProcessingService,
            measurement
        );
        if (!properties.requestedMicrophones?.length) {
            throw new Error("No microphone selected");
        }

        const result = await processor.getPsychoAcousticsCriticalBandSet(
            measurement,
            {
                requestedMicrophone: properties.requestedMicrophones[0],
                metricName: this.metricType,
            },
            progressCallback
        );

        if (!result || !result.results || result.results.length === 0) {
            throw new Error("Psychoacoustic spectrum set is empty");
        }
        if (!result.bandNr || result.bandNr.length === 0) {
            throw new Error("Psychoacoustic band numbers are empty");
        }

        // The result is in sub-bands, turn into full critical bands
        const criticalBandYAxisValues = result.bandNr.map((bandNr) =>
            PsychoAcousticBandUnits[this.metricType].convertSubBandToBand(bandNr)
        );

        // To prevent the rendering from freezing, we average the results over the desired step count.
        // This reduces the amount of data points to be rendered.
        // Additionally, we add padding to ensure the time axis corresponds to the measurement duration.
        const maxSamples = DrawImageProgram.MaxRenderResolution;
        const desiredStepCount = Math.min(maxSamples, result.results.length);
        const timeStepDuration = measurement.duration / desiredStepCount;

        let spectrumSet: SpectrumSetSpectrum[] = [];
        for (let timeStart = 0; timeStart < measurement.duration; timeStart += timeStepDuration) {
            const timeEnd = Math.min(timeStart + timeStepDuration, measurement.duration);

            // Find all results that overlap with this time window
            const relevantResults = result.results.filter(
                (spectrum) =>
                    spectrum.properties.timeRange.to > timeStart && spectrum.properties.timeRange.from < timeEnd
            );

            if (relevantResults.length === 0) {
                // If no data for this time window, add padding
                spectrumSet.push({
                    data: new Array(result.results[0].data.length).fill(0),
                    properties: {
                        timeRange: { from: timeStart, to: timeEnd },
                    },
                });
            } else {
                spectrumSet.push(this.averageResults(relevantResults, timeStart, timeEnd));
            }
        }

        const unitSpectrogram: UnitSpectrogram = {
            yAxisUnit: PsychoAcousticBandUnits[this.metricType],
            xAxisUnit: Time.second,
            yAxisValues: criticalBandYAxisValues,
            dataUnit: PsychoAcousticUnits[this.metricType],
            spectrumSet: spectrumSet,
        };

        return unitSpectrogram;
    }

    /**
     * Averages a set of spectrum results into a single SpectrumSetSpectrum object.
     * @param stepResults An array of SpectrumSetSpectrum objects to average.
     * @param timeStart The start time of the averaging window.
     * @param timeEnd The end time of the averaging window.
     * @returns A new SpectrumSetSpectrum object with averaged data and time range.
     */
    private averageResults(
        stepResults: SpectrumSetSpectrum[],
        timeStart: number,
        timeEnd: number
    ): SpectrumSetSpectrum {
        return {
            data: stepResults[0].data.map((_, dataIndex) => {
                const sum = stepResults.reduce((accumulator, result) => accumulator + result.data[dataIndex], 0);
                return sum / stepResults.length;
            }),
            properties: {
                timeRange: {
                    from: timeStart,
                    to: timeEnd,
                },
            },
        };
    }
}
